// ============================================================
// Moteur PUR du COMPTE DE RÉCUPÉRATION — AUCUNE dépendance Angular.
// Testé sans TestBed : hr-rcr.spec.ts.
// ============================================================
//
// « J'ai fait des heures en plus, je voudrais partir jeudi après-midi. »
//
// C'est le pendant du compteur (hr-counter.ts), vu du salarié et rendu ACTIONNABLE : les heures
// supplémentaires qu'on a décidé de remplacer par du repos (repos compensateur de remplacement,
// L3121-33) forment un stock, et ce stock se POSE comme un congé — même workflow que les CP :
// demande → validation du responsable → l'absence masque le planning.
//
// TROIS MASSES, et la distinction compte :
//   ACQUIS   — les heures converties en repos, majoration comprise (1 h à +25 % ⇒ 1 h 15) ;
//   PRIS     — les récupérations APPROUVÉES, valorisées au planning qu'elles effacent ;
//   ENGAGÉ   — les demandes en attente de réponse.
//
// DISPONIBLE = ACQUIS − PRIS − ENGAGÉ. Compter l'engagé est ce qui empêche de poser trois fois
// le même solde en attendant que le responsable réponde — l'erreur qu'aucun salarié ne fait
// exprès et que tout le monde fait une fois.

import { C_value } from '@app/shared/_models/common';
import { HrAbsence } from '@app/shared/_models/hr';
import {
	HR_ABSENCE_DURATION_CUSTOM, HR_ABSENCE_DURATION_HALF, HR_ABSENCE_STATE_APPROVED,
	HR_ABSENCE_STATE_REQUESTED,
} from '@app/shared/constants';
import { EffectiveSlot, addDays, halfDayMinutes, plannedMinutes } from './hr-schedule-engine';

// Les types d'absence qui DÉBITENT le compteur de récupération (c_values 23, `data.rcr = true`).
// Un établissement peut en avoir plusieurs (« Récupération », « RCR »), ou aucun tant que le
// repos compensateur n'est pas en place.
export function rcrAbsenceTypes(types: C_value[]): number[] {
	return types
		.filter(t => t.data?.rcr === true && t.is_active !== false && t.id != null)
		.map(t => t.id as number);
}

export function isRcrType(typ: number | null | undefined, types: C_value[]): boolean {
	return typ != null && rcrAbsenceTypes(types).includes(typ);
}

// Les types qui soldent la CONTREPARTIE OBLIGATOIRE EN REPOS (`data.cor = true`). Séparés des
// précédents parce que le droit les sépare : la contrepartie se prend dans les deux mois
// (D3121-19), et c'est un repos qui S'AJOUTE au paiement de l'heure au lieu de le remplacer.
export function corAbsenceTypes(types: C_value[]): number[] {
	return types
		.filter(t => t.data?.cor === true && t.is_active !== false && t.id != null)
		.map(t => t.id as number);
}

/// La moitié de journée visée par une demande de demi-journée (`data.half`), si elle est précisée.
export function absenceHalf(a: { duration_kind?: number; data?: any } | null | undefined): 'morning' | 'afternoon' | null {
	if (!a || a.duration_kind !== HR_ABSENCE_DURATION_HALF) return null;
	const h = a.data?.half;
	return h === 'morning' || h === 'afternoon' ? h : null;
}

/// Ce qu'une demande COÛTE au compteur, en minutes : le temps de travail qu'elle retire du
/// planning. Une demi-journée ne vaut pas un forfait — jeudi après-midi peut faire 3 h chez l'un
/// et 5 h chez l'autre, et c'est bien ce temps-là qui est récupéré.
///
/// Une demande en HEURES (`duration_kind = 3`) vaut ses heures : c'est le seul cas où le salarié
/// annonce lui-même la durée, et le planning n'a pas à le contredire.
export function rcrCostOfRequest(
	request: { datestart: string; dateend: string; duration_kind?: number; hours?: number | null; data?: any },
	slots: EffectiveSlot[],			// créneaux THÉORIQUES du salarié (absences non appliquées)
): number {
	const { datestart, dateend } = request;
	if (!datestart || !dateend || dateend < datestart) return 0;

	if (request.duration_kind === HR_ABSENCE_DURATION_CUSTOM) {
		return Math.max(0, Math.round((request.hours ?? 0) * 60));
	}

	const half = absenceHalf(request);
	let total = 0;
	for (let dat = datestart; dat <= dateend; dat = addDays(dat, 1)) {
		const daySlots = slots.filter(s => s.dat === dat);
		total += half ? halfDayMinutes(daySlots, half) : plannedMinutes(daySlots);
	}
	return total;
}

export interface RcrAccount {
	credited_min: number;			// acquis (heures sup converties en repos, majoration comprise)
	taken_min: number;				// récupérations APPROUVÉES
	pending_min: number;			// demandes en attente de réponse
	available_min: number;		// ce qu'on peut encore poser aujourd'hui
}

/// Le compte de récupération d'UN salarié : ce qu'il a, ce qu'il a déjà posé, ce qui lui reste.
///
/// `credited_min` vient du compteur (hr-counter → `rcr_credited_min`), qui l'a lui-même calculé
/// depuis les arbitrages « repos » des heures supplémentaires. On ne le recalcule pas ici : une
/// seule source, sinon deux écrans finissent par annoncer deux soldes.
export function rcrAccountOf(
	creditedMin: number,
	absences: HrAbsence[],			// TOUTES les absences du salarié (tous états)
	rcrTypes: number[],
	slots: EffectiveSlot[],			// planning théorique couvrant les absences considérées
): RcrAccount {
	const mine = absences.filter(a => a.typ != null && rcrTypes.includes(a.typ));
	const sum = (state: number) => mine
		.filter(a => a.state === state)
		.reduce((total, a) => total + rcrCostOfRequest(a, slots), 0);

	const credited = Math.max(0, creditedMin);
	const taken = sum(HR_ABSENCE_STATE_APPROVED);
	const pending = sum(HR_ABSENCE_STATE_REQUESTED);
	return {
		credited_min: credited,
		taken_min: taken,
		pending_min: pending,
		available_min: credited - taken - pending,
	};
}

/// Une demande de récupération tient-elle dans le solde ? Rend le motif de refus, ou null.
/// C'est un GARDE-FOU de saisie, pas une règle de droit : le responsable garde la main, et un
/// solde à zéro n'empêche pas de poser un congé d'un autre type.
export function rcrRequestIssue(costMin: number, account: RcrAccount): string | null {
	if (costMin <= 0) return 'Cette demande ne retire aucune heure du planning : rien à récupérer.';
	if (costMin > account.available_min) {
		return 'Solde insuffisant : ' + rcrLabel(account.available_min) + ' disponible'
			+ (account.pending_min > 0 ? ' (' + rcrLabel(account.pending_min) + ' déjà en attente)' : '')
			+ ', ' + rcrLabel(costMin) + ' demandé.';
	}
	return null;
}

/// « 7h30 » / « 0h » — durée en heures et minutes, jamais signée (un solde de récup n'est pas
/// un compteur d'écart : il ne descend pas sous zéro sans que quelqu'un l'ait décidé).
export function rcrLabel(minutes: number): string {
	const abs = Math.abs(Math.round(minutes));
	const h = Math.floor(abs / 60);
	const m = abs % 60;
	return (minutes < 0 ? '−' : '') + h + 'h' + (m > 0 ? (m < 10 ? '0' + m : '' + m) : '');
}
